import React, { useEffect, useState } from "react";
import { Box, Heading, HStack, Select, Input, VStack, Card, CardHeader, CardBody, Text, Switch, useToast, Button } from "@chakra-ui/react";
import client from "../../../helpers/Client";

const ROLES = [
  { value: "manager", label: "Yönetici" },
  { value: "servicer", label: "Servisçi" },
  { value: "technical", label: "Teknik" },
  { value: "reseller", label: "Bayi" },
  { value: "employee", label: "Personel" },
];

// Basit, anlaşılır liste: her sayfa bir kart, tek toggle
const SECTIONS = [
  { to: "/ana-sayfa/", title: "Ana Sayfa", desc: "Genel gösterge paneli" },
  { to: "/calisanlar/", title: "Personellar", desc: "Personel ve roller" },
  { to: "/musteriler/", title: "Müşteriler", desc: "Müşteri kayıtları" },
  { to: "/proje/", title: "Projeler", desc: "Proje listesi ve yönetim" },
  { to: "/bayi/", title: "Bayi", desc: "Bayi ve ekipler" },
  { to: "/is-emri/", title: "İş Emri", desc: "İş emirleri ve durumlar" },
  { to: "/tamamlanmis-is-emri/", title: "Tamamlanmış İş Emri", desc: "Tamamlanan işler" },
  { to: "/gorevler/", title: "Görevler", desc: "Görev ve iş akışları" },
  { to: "/randevular/", title: "Randevular", desc: "Randevu planlama" },
  { to: "/servis/", title: "Servis Kayıtları", desc: "Servis işlemleri" },
  { to: "/is-emri/olustur/", title: "Yeni İş Emri", desc: "Yeni kayıt oluştur" },
];

const BUTTONS = [
  { route: "/is-emri/", id: "ticket.action.start", title: "İş Emrini Başlat" },
  { route: "/is-emri/", id: "ticket.action.complete", title: "İş Emrini Tamamla" },
  { route: "/is-emri/", id: "ticket.action.cancel", title: "İş Emrini İptal Et" },
  { route: "/is-emri/", id: "ticket.action.go_subtasks", title: "Alt Görevlere Git" },
  { route: "/gorevler/", id: "task.action.start", title: "Görevi Başlat" },
  { route: "/gorevler/", id: "task.action.complete", title: "Görevi Tamamla" },
  { route: "/gorevler/", id: "task.action.approve", title: "Görevi Onayla" },
  { route: "/gorevler/", id: "task.action.set_type", title: "Görev Tipini Ayarla" },
  { route: "/gorevler/", id: "task.action.set_appointment", title: "Randevu Ayarla" },
  { route: "/gorevler/", id: "task.action.cancel_appointment", title: "Randevuyu İptal Et" },
  { route: "/gorevler/", id: "task.action.set_service", title: "Servis Kaydı Oluştur" },
  { route: "/gorevler/", id: "task.action.assign_team", title: "Takım Ata" },
  { route: "/gorevler/", id: "task.action.create_subtask", title: "Alt Görev Ekle" },
  { route: "/gorevler/", id: "task.action.delete", title: "Görevi Sil" },
  { route: "/servis/", id: "service.action.received_by_base", title: "Servise Ulaştı" },
  { route: "/servis/", id: "service.action.eval_started", title: "İncelemeye Al" },
  { route: "/servis/", id: "service.action.eval_completed", title: "Servis Tutarı Belirle" },
  { route: "/servis/", id: "service.action.coverage_accept", title: "Ücret Onaylandı" },
  { route: "/servis/", id: "service.action.coverage_reject", title: "Ücret Reddedildi" },
  { route: "/servis/", id: "service.action.send_to_reseller", title: "Bayiye Gönderildi" },
  { route: "/servis/", id: "service.action.received_by_reseller", title: "Teslim Alındı" },
  { route: "/servis/", id: "service.action.completed", title: "Ürün Çalıştı" },
  { route: "/servis/", id: "service.action.failed", title: "Ürün Çalışmadı" },
];

export default function RolePagesList() {
  const toast = useToast();
  const [role, setRole] = useState("manager");
  const [query, setQuery] = useState("");
  const [denyList, setDenyList] = useState([]); // enabled=false kayıtlar
  const [loading, setLoading] = useState(true);

  const fetchDeny = async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`/config/role-pages/?enabled=false&role_code=${role}`);
      const arr = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
      setDenyList(arr);
    } catch (e) {
      toast({ title: "Veri alınamadı", status: "error" });
      setDenyList([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchDeny(); }, [role]);

  const isDisabled = (route) => denyList.some(d => d.route === route && d.enabled === false);
  const findEntry = (route) => denyList.find(d => d.route === route);
  const isButtonDisabled = (id) => denyList.some(d => d.button_id === id && d.enabled === false && (!!d.route ? true : true));
  const findButtonEntry = (id) => denyList.find(d => d.button_id === id);

  const onToggle = async (route, nextOn) => {
    try {
      const existing = findEntry(route);
      if (nextOn) {
        // aç: varsa deny kaydı sil
        if (existing) await client.delete(`/config/role-pages/${existing.id}/`);
      } else {
        // kapa: yoksa oluştur, varsa patch
        if (!existing) await client.post(`/config/role-pages/`, { role_code: role, route, label: "", enabled: false });
        else await client.patch(`/config/role-pages/${existing.id}/`, { enabled: false });
      }
      fetchDeny();
    } catch (e) {
      toast({ title: "Güncelleme hatası", status: "error" });
    }
  };

  const onToggleButton = async (btn, nextOn) => {
    try {
      const existing = findButtonEntry(btn.id);
      if (nextOn) {
        if (existing) await client.delete(`/config/role-pages/${existing.id}/`);
      } else {
        if (!existing) await client.post(`/config/role-pages/`, { role_code: role, route: btn.route, button_id: btn.id, label: "", enabled: false });
        else await client.patch(`/config/role-pages/${existing.id}/`, { enabled: false, button_id: btn.id, route: btn.route });
      }
      fetchDeny();
    } catch (e) {
      toast({ title: "Güncelleme hatası", status: "error" });
    }
  };

  const filtered = SECTIONS.filter(s => (
    s.title.toLowerCase().includes(query.toLowerCase()) || s.to.toLowerCase().includes(query.toLowerCase())
  ));

  return (
    <Box p={6}>
      <Heading size="md" mb={3}>Rol Bazlı Görünür Sayfalar (Liste)</Heading>
      <HStack mb={4} spacing={3}>
        <Select value={role} onChange={(e) => setRole(e.target.value)} w="220px">
          {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </Select>
        <Input placeholder="Ara: sayfa/route" value={query} onChange={(e) => setQuery(e.target.value)} />
        <Button onClick={fetchDeny} isLoading={loading}>Yenile</Button>
      </HStack>

      <VStack align="stretch" spacing={3}>
        {filtered.map(sec => {
          const disabled = isDisabled(sec.to);
          const checked = !disabled;
          return (
            <Card key={sec.to} variant="outline">
              <CardHeader>
                <HStack justify="space-between">
                  <Text fontWeight="bold">{sec.title}</Text>
                  <HStack>
                    <Text color="gray.600">{checked ? "Açık" : "Kapalı"}</Text>
                    <Switch isChecked={checked} onChange={(e) => onToggle(sec.to, e.target.checked)} />
                  </HStack>
                </HStack>
              </CardHeader>
              <CardBody>
                <Text fontSize="sm" color="gray.600">{sec.desc}</Text>
                <Text fontSize="xs" color="gray.500">Route: {sec.to}</Text>
              </CardBody>
            </Card>
          );
        })}
      </VStack>

      <Heading size="md" mt={8} mb={3}>Rol Bazlı Buton Görünürlüğü</Heading>
      <VStack align="stretch" spacing={3}>
        {BUTTONS.map(btn => {
          const disabled = isButtonDisabled(btn.id);
          const checked = !disabled;
          return (
            <Card key={btn.id} variant="outline">
              <CardHeader>
                <HStack justify="space-between">
                  <Text fontWeight="bold">{btn.title}</Text>
                  <HStack>
                    <Text color="gray.600">{checked ? "Açık" : "Kapalı"}</Text>
                    <Switch isChecked={checked} onChange={(e) => onToggleButton(btn, e.target.checked)} />
                  </HStack>
                </HStack>
              </CardHeader>
              <CardBody>
                <Text fontSize="xs" color="gray.500">Route: {btn.route}</Text>
                <Text fontSize="xs" color="gray.500">Button ID: {btn.id}</Text>
              </CardBody>
            </Card>
          );
        })}
      </VStack>
    </Box>
  );
}